import React from 'react';

import PropTypes from 'prop-types';
import { Select } from 'semantic-ui-react';

import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _isNumber from 'lodash/isNumber';
import { useDispatch, useSelector } from 'react-redux';

import { LOCATION_SELECT_PLACEHOLDER, ALL_LOCATIONS_VALUE } from './LocationSelect.constants';
import { getLocationOptions, getFinalLocations } from './LocationSelect.utils';
import { setActiveLocation } from '../../../../../actions/ecommerce';
import { LocationPropType } from '../../../../../libs/proptypes';
import { selectActiveLocation, selectBusinessLocations } from '../../../../../selectors';

import './LocationSelect.scss';

const propTypes = {
  locations: PropTypes.arrayOf(LocationPropType),
  activeLocation: LocationPropType,
  onChangeLocation: PropTypes.func,
  global: PropTypes.bool,
  hideAllLocationsOption: PropTypes.bool,
  placeholder: PropTypes.string,
  fluid: PropTypes.bool,
  clearable: PropTypes.bool,
  hideIfSingleLocation: PropTypes.bool,
  filterLocations: PropTypes.func,
};

const defaultProps = {
  locations: [],
  activeLocation: null,
  onChangeLocation: () => null,
  global: true,
  hideAllLocationsOption: false,
  placeholder: LOCATION_SELECT_PLACEHOLDER,
  fluid: false,
  clearable: false,
  hideIfSingleLocation: false,
  filterLocations: null,
};

const LocationSelect = ({
  activeLocation,
  locations,
  onChangeLocation,
  global,
  hideAllLocationsOption,
  placeholder,
  fluid,
  clearable,
  hideIfSingleLocation,
  filterLocations,
}) => {
  const dispatch = useDispatch();
  const globalLocations = useSelector(selectBusinessLocations);
  const globalActiveLocation = useSelector(selectActiveLocation);
  const baseFinalLocations = getFinalLocations(global, globalLocations, locations);
  const finalLocations = filterLocations
    ? baseFinalLocations.filter(filterLocations)
    : baseFinalLocations;
  const finalActiveLocation = global ? globalActiveLocation : activeLocation;
  const onChangeHandler = global
    ? (payload) => dispatch(setActiveLocation(payload))
    : onChangeLocation;
  const locationOptions = getLocationOptions(finalLocations, { hideAllLocationsOption });

  function onChangeLocationWrapper(e, { value: targetLocationId }) {
    const location = _find(finalLocations, ({ id }) => id === targetLocationId);
    onChangeHandler(location);
  }

  function isAllLocations() {
    if (_isNumber(finalActiveLocation)) {
      return false;
    }
    if (_isNil(finalActiveLocation)) {
      return true;
    }
    return Object.keys(finalActiveLocation).length === 0;
  }

  function getValue() {
    if (isAllLocations()) {
      return ALL_LOCATIONS_VALUE;
    }
    if (_isNumber(finalActiveLocation)) {
      return finalActiveLocation;
    }
    return finalActiveLocation.id;
  }

  if (_isEmpty(finalLocations)) {
    return null;
  }

  if (hideIfSingleLocation && finalLocations.length === 1) {
    return null;
  }

  return (
    <Select
      placeholder={placeholder}
      value={getValue()}
      onChange={onChangeLocationWrapper}
      options={locationOptions}
      className="location-select"
      fluid={fluid}
      clearable={clearable}
    />
  );
};

LocationSelect.propTypes = propTypes;
LocationSelect.defaultProps = defaultProps;

export default LocationSelect;
